import { API_BASE, REFRESH_TIMEOUT_MS } from '../api/config'
import type { LoginResponse } from '../api/types'
import { tokenStore } from './tokenStore'

export type RefreshResult = 'ok' | 'unauthenticated' | 'network'
let inflight: Promise<RefreshResult> | null = null

function transient(): RefreshResult {
  // A network failure or 5xx must NEVER clear an already-authenticated session — only a definitive
  // 401/403 from the server means the session is actually gone.
  if (tokenStore.getSnapshot().status !== 'authenticated') tokenStore.clear('network')
  return 'network'
}

/** Every tab refreshes on load with the same httpOnly cookie, and the server rotates that cookie on use.
 * The in-flight guard above only covers one tab, so tabs restored together are serialised through a
 * cross-tab Web Lock: the second one waits, then refreshes with the cookie the first one was just given.
 * (The API also tolerates a lost race for a few seconds, so a browser without Web Locks still recovers.) */
const CROSS_TAB_LOCK = 'crewpresence:auth-refresh'

function withCrossTabLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined
  if (!locks) return fn()
  // `fn` never rejects (it maps every failure to a RefreshResult), so a rejection here means the lock
  // API itself failed — an insecure context, say — and the refresh just runs unserialised instead.
  return locks.request(CROSS_TAB_LOCK, fn).catch(fn)
}

export function refreshSession(): Promise<RefreshResult> {
  if (inflight) return inflight
  inflight = withCrossTabLock(async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        signal: AbortSignal.timeout(REFRESH_TIMEOUT_MS),
      })
      if (res.status === 401 || res.status === 403) {
        tokenStore.clear()
        return 'unauthenticated'
      }
      if (!res.ok) return transient() // 5xx / 502 while the backend restarts must not clear a session
      tokenStore.setSession((await res.json()) as LoginResponse)
      return 'ok'
    } catch {
      return transient() // offline / timeout
    } finally {
      inflight = null
    }
  })
  return inflight
}
